import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppShell, ConnectedNotificationBell, CreditsChip, FeedbackButton,
  defaultNavGroups,
} from '../components/shell';
import { useAuth } from '../contexts/AuthContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { useCreditRequest } from '../hooks/useCreditRequest';
import { useOrgCredits } from '../hooks/useOrgCredits';

/**
 * Connected v2 shell (TASK-105) — wires AppShell (presentational) to auth +
 * router so pages stay thin. Replaces the old Layout/Header/Sidebar chrome.
 * The wizard keeps its own chrome (TASK-106).
 */
interface ShellPageProps {
  title: string;
  /** Sidebar item to highlight (see `defaultNavGroups` ids). */
  activeId: string;
  /** RFQ count badge in the nav; omit while loading. */
  rfqCount?: number;
  /** Override the top-bar search placeholder; omit for the standard prompt. */
  searchPlaceholder?: string;
  onCreateRFQ: () => void;
  contentClassName?: string;
  children: React.ReactNode;
}

const routeOf: Record<string, string> = {
  overview: '/dashboard',
  rfqs: '/rfqs',
};

const ShellPage: React.FC<ShellPageProps> = ({
  title, activeId, rfqCount, searchPlaceholder, onCreateRFQ, contentClassName, children,
}) => {
  const navigate = useNavigate();
  const { logout, currentUser, backendUser } = useAuth();
  const { openFeedback } = useFeedback();
  const orgCredits = useOrgCredits();
  const creditRequest = useCreditRequest();

  const name = backendUser?.fullName || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User';
  const date = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
  const groups = defaultNavGroups.map((g) => ({
    ...g,
    items: g.items.map((item) => (item.id === 'rfqs' ? { ...item, count: rfqCount } : item)),
  }));

  return (
    <AppShell
      sidebar={{
        groups,
        activeId,
        // Feedback is the one nav id that is an action, not a destination:
        // it opens Robbie's panel over the page you are already on.
        onNavigate: (id) => {
          if (routeOf[id]) navigate(routeOf[id]);
        },
        user: { name, role: backendUser?.displayRole },
        footerSlot: (
          <CreditsChip
            credits={orgCredits.credits}
            loading={orgCredits.loading}
            unreachable={orgCredits.unreachable}
            creditRequest={creditRequest}
          />
        ),
        onLogout: () => logout().catch((err: unknown) => console.error('Failed to logout:', err)),
      }}
      topBar={{
        title,
        date,
        searchPlaceholder,
        onAction: onCreateRFQ,
        feedbackSlot: <FeedbackButton onClick={() => openFeedback('top-bar')} />,
        notificationsSlot: <ConnectedNotificationBell />,
      }}
      contentClassName={contentClassName}
    >
      {children}
    </AppShell>
  );
};

export default ShellPage;
